/** 2D 아핀 행렬. x' = a·x + c·y + e, y' = b·x + d·y + f (Canvas 2D setTransform 과 같은 순서). */
export interface Matrix {
  readonly a: number;
  readonly b: number;
  readonly c: number;
  readonly d: number;
  readonly e: number;
  readonly f: number;
}

export interface Point2 {
  readonly x: number;
  readonly y: number;
}

export const IDENTITY: Matrix = { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };

/** m ∘ n — n 을 먼저 적용하고 m 을 적용한다. */
export const multiply = (m: Matrix, n: Matrix): Matrix => ({
  a: m.a * n.a + m.c * n.b,
  b: m.b * n.a + m.d * n.b,
  c: m.a * n.c + m.c * n.d,
  d: m.b * n.c + m.d * n.d,
  e: m.a * n.e + m.c * n.f + m.e,
  f: m.b * n.e + m.d * n.f + m.f,
});

/** compose(T, R, S) = T·R·S — 오른쪽(S)이 가장 먼저 적용된다. */
export const compose = (...matrices: readonly Matrix[]): Matrix =>
  matrices.reduce((acc, m) => multiply(acc, m), IDENTITY);

export const translate = (tx: number, ty: number): Matrix => ({
  a: 1,
  b: 0,
  c: 0,
  d: 1,
  e: tx,
  f: ty,
});

export const scale = (sx: number, sy: number): Matrix => ({ a: sx, b: 0, c: 0, d: sy, e: 0, f: 0 });

/** 시계 방향 회전(도). y 가 아래인 좌표계에서 (1,0) 이 (cos, sin) 으로 간다. */
export const rotateDeg = (deg: number): Matrix => {
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  return { a: cos, b: sin, c: -sin, d: cos, e: 0, f: 0 };
};

export const apply = (m: Matrix, p: Point2): Point2 => ({
  x: m.a * p.x + m.c * p.y + m.e,
  y: m.b * p.x + m.d * p.y + m.f,
});

/** 역행렬. 행렬식이 0 이거나 유한수가 아니면 null. */
export const invert = (m: Matrix): Matrix | null => {
  const det = m.a * m.d - m.b * m.c;
  if (!Number.isFinite(det) || det === 0) return null;
  return {
    a: m.d / det,
    b: -m.b / det,
    c: -m.c / det,
    d: m.a / det,
    e: (m.c * m.f - m.d * m.e) / det,
    f: (m.b * m.e - m.a * m.f) / det,
  };
};
